/* ScrollFloat animation ported from React Bits, not a CSS approximation.
 * Copyright (c) 2026 David Haz. MIT + Commons Clause; see loc-react-bits-LICENSE.txt.
 * Original GSAP values retained; Shopify lifecycle, word wrapping and a11y added. */
(() => {
  if (window.LocScrollFloat || !window.gsap || !window.ScrollTrigger) return;
  gsap.registerPlugin(ScrollTrigger);
  const controllers = new Map();
  class ScrollFloatController {
    constructor(root) {
      this.root = root; this.records = new Map(); this.destroyed = false;
      this.media = matchMedia('(prefers-reduced-motion: reduce)');
      this.abort = new AbortController();
      this.refresh = this.refresh.bind(this);
      this.media.addEventListener('change', () => {
        this.records.forEach(record => this.restore(record));
        this.records.clear(); this.scan(); this.refresh();
      }, { signal: this.abort.signal });
      this.resize = new ResizeObserver(this.refresh); this.resize.observe(root);
      this.mutations = new MutationObserver(changes => {
        if (changes.some(c => c.target.matches('.loc-ip__panel, [data-ip-panel]'))) this.refresh();
      });
      this.mutations.observe(root, { subtree: true, attributes: true, attributeFilter: ['class', 'hidden'] });
      root.addEventListener('transitionend', e => {
        if (e.target.matches('.loc-ip__panel, [data-ip-panel]')) this.refresh();
      }, { signal: this.abort.signal });
      this.scan(); this.refresh();
      document.fonts?.ready.then(() => { if (!this.destroyed) this.refresh(); });
      root.dataset.locScrollFloat = 'ready';
    }
    scan() { this.root.querySelectorAll('[data-loc-split-scroll]').forEach(el => this.set(el, el.textContent)); }
    set(el, source) {
      if (this.destroyed) return;
      const previous = this.records.get(el);
      if (previous?.source === source) return;
      if (previous) this.restore(previous);
      const record = { el, source, aria: el.getAttribute('aria-label'), chars: [], context: null };
      this.records.set(el, record);
      if (this.media.matches) return;
      const visual = document.createElement('span');
      visual.className = 'loc-split-visual'; visual.setAttribute('aria-hidden', 'true');
      const segmenter = typeof Intl.Segmenter === 'function' ? new Intl.Segmenter(undefined, { granularity: 'grapheme' }) : null;
      for (const segment of source.replace(/\r\n?/g, '\n').match(/\s+|[^\s]+/gu) || []) {
        if (/^\s+$/u.test(segment)) {
          segment.split(/(\n)/).forEach(part => { if (part) visual.append(part === '\n' ? document.createElement('br') : document.createTextNode(part)); });
          continue;
        }
        const word = document.createElement('span'); word.className = 'loc-rb-word';
        const chars = segmenter ? [...segmenter.segment(segment)].map(s => s.segment) : Array.from(segment);
        chars.forEach(char => {
          const span = document.createElement('span'); span.className = 'loc-rb-char'; span.textContent = char;
          record.chars.push(span); word.append(span);
        });
        visual.append(word);
      }
      el.replaceChildren(visual); el.setAttribute('aria-label', source.replace(/\s+/g, ' ').trim());
      this.install(record);
    }
    install(record) {
      if (record.context || this.media.matches || !record.chars.length || !record.el.getClientRects().length || record.el.getBoundingClientRect().width === 0) return;
      const nested = this.root.closest('.page-wrapper');
      const scroller = nested && nested.scrollHeight > nested.clientHeight + 2 && /^(auto|scroll)$/.test(getComputedStyle(nested).overflowY) ? nested : window;
      record.context = gsap.context(() => {
        // Exact ScrollFloat from/to, easing, stagger, start/end and scrub values.
        record.tween = gsap.fromTo(record.chars, {
          willChange: 'opacity, transform', opacity: 0, yPercent: 120,
          scaleY: 2.3, scaleX: .7, transformOrigin: '50% 0%'
        }, {
          duration: 1, ease: 'back.inOut(2)', opacity: 1, yPercent: 0,
          scaleY: 1, scaleX: 1, stagger: .03,
          scrollTrigger: {
            trigger: record.el, scroller, start: 'center bottom+=50%',
            end: 'bottom bottom-=40%', scrub: true,
            id: 'loc-scroll-float-' + this.root.id + '-' + [...this.records.keys()].indexOf(record.el)
          }
        });
      }, record.el);
      // Resolve the restored scroll position in the same task as splitting the text.
      // The deferred global refresh must not leave already-painted headings at opacity zero.
      record.tween.scrollTrigger.refresh();
      record.tween.totalProgress(record.tween.scrollTrigger.progress, true);
    }
    refresh() {
      if (this.destroyed) return;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        if (this.destroyed || !this.root.isConnected) return;
        this.records.forEach(record => this.install(record));
        ScrollTrigger.refresh();
      }, 80);
    }
    restore(record) {
      record.context?.revert();
      record.el.replaceChildren(document.createTextNode(record.source));
      if (record.aria === null) record.el.removeAttribute('aria-label');
      else record.el.setAttribute('aria-label', record.aria);
    }
    destroy() {
      this.destroyed = true; clearTimeout(this.timer); this.abort.abort();
      this.resize.disconnect(); this.mutations.disconnect();
      this.records.forEach(record => this.restore(record)); this.records.clear();
      delete this.root.dataset.locScrollFloat;
    }
  }
  function scan() {
    if (!document.body.classList.contains('loc-dark-home')) return;
    document.querySelectorAll('.loc-dark-home [data-loc-initialized]').forEach(root => {
      if (!controllers.has(root)) controllers.set(root, new ScrollFloatController(root));
    });
    controllers.forEach((controller, root) => { if (!root.isConnected) { controller.destroy(); controllers.delete(root); } });
  }
  window.LocScrollFloat = {
    register(root, el) { controllers.get(root)?.set(el, el.textContent); },
    refresh(root) { controllers.get(root)?.refresh(); },
    controllers
  };
  scan();
  document.addEventListener('shopify:section:load', () => requestAnimationFrame(scan));
  document.addEventListener('shopify:section:unload', e => {
    controllers.forEach((controller, root) => {
      if (root === e.target || e.target.contains(root)) { controller.destroy(); controllers.delete(root); }
    });
  });
})();
